import { useEffect, useRef } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import '@/App.css';
import HomePage from '@/pages/HomePage';
import FeaturesPage from '@/pages/FeaturesPage';
import PricingPage from '@/pages/PricingPage';
import AboutPage from '@/pages/AboutPage';
import ContactPage from '@/pages/ContactPage';
import DemoPage from '@/pages/DemoPage';
import RegisterPage from '@/pages/RegisterPage';
import SchoolWebsitePage from '@/pages/SchoolWebsitePage';
import CollegeFeaturesPage from '@/pages/CollegeFeaturesPage';
import CollegeWebsitePage from '@/pages/CollegeWebsitePage';
import LearningManagementPage from '@/pages/LearningManagementPage';
import CoachingPage from '@/pages/CoachingPage';
import AIChatbotPage from '@/pages/AIChatbotPage';
import AdminPage from '@/pages/AdminPage';
import AdminLoginPage from '@/pages/AdminLoginPage';
import NotFoundPage from '@/pages/NotFoundPage';
import PrivacyPage from '@/pages/PrivacyPage';
import TermsAndConditionPage from '@/pages/TermsAndConditionPage';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import WhatsAppButton from '@/components/WhatsAppButton';
import { useLocation } from 'react-router-dom';

const ADMIN_AUTH_KEY = 'isAdminAuthenticated';

const isAdminAuthenticated = () => {
  return localStorage.getItem(ADMIN_AUTH_KEY) === 'true';
};

function ProtectedAdminRoute({ children }) {
  if (!isAdminAuthenticated()) {
    return <Navigate to="/admin-login" replace />;
  }

  return children;
}

function PublicAdminLoginRoute({ children }) {
  if (isAdminAuthenticated()) {
    return <Navigate to="/admin" replace />;
  }

  return children;
}

const GA_MEASUREMENT_ID = 'G-6F1VV7EBDR';

function GoogleAnalytics() {
  const location = useLocation();
  const isFirstLoad = useRef(true);

  useEffect(() => {
    if (typeof window.gtag !== 'function') {
      return;
    }

    // Initial page_view is already sent by the gtag snippet in index.html.
    if (isFirstLoad.current) {
      isFirstLoad.current = false;
      return;
    }

    window.gtag('config', GA_MEASUREMENT_ID, {
      page_path: location.pathname + location.search,
    });
  }, [location.pathname, location.search]);

  return null;
}

function AppRoutes() {
  const location = useLocation();
  const hideSiteLayout = location.pathname === '/admin-login';

  return (
    <>
      <GoogleAnalytics />
      {!hideSiteLayout && <Navbar />}
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/services/features" element={<FeaturesPage />} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/demo" element={<DemoPage />} />
        <Route
          path="/services/school-website"
          element={<SchoolWebsitePage />}
        />
        <Route
          path="/services/college-features"
          element={<CollegeFeaturesPage />}
        />
        <Route
          path="/services/college-website"
          element={<CollegeWebsitePage />}
        />
        <Route
          path="/services/learning-management"
          element={<LearningManagementPage />}
        />
        <Route path="/services/coaching" element={<CoachingPage />} />
        <Route path="/services/ai-chatbot" element={<AIChatbotPage />} />
        <Route
          path="/admin-login"
          element={
            <PublicAdminLoginRoute>
              <AdminLoginPage />
            </PublicAdminLoginRoute>
          }
        />
        <Route
          path="/admin"
          element={
            <ProtectedAdminRoute>
              <AdminPage />
            </ProtectedAdminRoute>
          }
        />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/terms-and-condition" element={<TermsAndConditionPage />} />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
      {!hideSiteLayout && <Footer />}
      {!hideSiteLayout && <WhatsAppButton />}
    </>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AppRoutes />
      </BrowserRouter>
    </div>
  );
}

export default App;
